import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './SubtitleDisplay.css';

// A bilingual dialogue line (currently only tamil-cave's roomDialogues.js) is written as one
// string, Tamil sentence + "\n" + its English translation. Splitting on the FIRST newline only
// (not every line) keeps a line's own internal line breaks, if it has any, intact within
// whichever half is shown. Falls back to the whole string when there's no embedded newline -
// every other room's dialogue text, which has no bilingual convention at all.
function pickBilingualLine(text, language) {
  if (typeof text !== 'string') return text;
  const splitAt = text.indexOf('\n');
  if (splitAt === -1) return text;
  return language === 'en' ? text.slice(splitAt + 1) : text.slice(0, splitAt);
}

/**
 * SubtitleDisplay Component
 * Displays screenplay dialogues as animated floating text subtitles
 *
 * Props:
 * - dialogues: Array of dialogue objects [{text, duration, character}]
 * - onComplete: Callback when all dialogues finish
 * - skipEnabled: (unused, kept for compatibility)
 * - language: Optional 'ta' | 'en' - when passed, shows only that half of a bilingual
 *   "தமிழ்\nEnglish" dialogue line instead of both at once (see pickBilingualLine above).
 *   Omitted entirely by every room without a language toggle, which keeps rendering the full
 *   text exactly as before.
 */
function SubtitleDisplay({ dialogues = [], onComplete, skipEnabled = true, language = null }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef(null);
  const prevDialoguesRef = useRef(null);

  const currentDialogue = dialogues[currentIndex];
  const isLastLine = dialogues.length > 0 && currentIndex === dialogues.length - 1;

  // Single effect for both "reset on a new sequence" and "show/advance" -
  // these used to be two separate effects, which both fire on mount (and
  // both fire whenever `dialogues` changes); since they ran in declaration
  // order within the same passive-effect flush, the reset effect's
  // `setIsVisible(false)` always landed *after* this one's `setIsVisible(true)`
  // and silently clobbered it, so line 0 of every sequence rendered as blank
  // "dead air" until the queued timeout advanced to line 1. Keeping this as
  // one effect makes that ordering race impossible.
  useEffect(() => {
    if (!dialogues || dialogues.length === 0) return;

    // A new dialogue sequence was handed in - start over from line 0 before
    // showing anything. This effect re-runs once currentIndex catches up.
    if (prevDialoguesRef.current !== dialogues) {
      prevDialoguesRef.current = dialogues;
      if (currentIndex !== 0) {
        setCurrentIndex(0);
        return;
      }
    }

    // Show current dialogue
    setIsVisible(true);

    // The last line of a sequence is frequently the actual clue/instruction a
    // player needs (what's still locked, what to go find) - auto-hiding it on
    // a timer meant players who read slower lost it before finishing, with no
    // way to bring it back, right when they most needed it. It now stays up
    // indefinitely instead of racing a clock; handleDismissLast (wired to a
    // tap/click below) is the only thing that dismisses it and fires
    // onComplete. Earlier lines in a multi-line exchange keep auto-advancing
    // on their timer as before.
    if (isLastLine) {
      return undefined;
    }

    const displayDuration = (currentDialogue?.duration || 3000) * 1.5;
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);

      // Wait for fade out animation before moving to next
      setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
      }, 500); // Match CSS transition duration

    }, displayDuration);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [currentIndex, dialogues, currentDialogue, isLastLine]);

  const handleDismissLast = () => {
    if (!isLastLine) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
    setTimeout(() => {
      if (onComplete) {
        onComplete();
      }
    }, 500);
  };

  const handleSkip = () => {
    if (!skipEnabled) return;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    setIsVisible(false);
    
    if (currentIndex < dialogues.length - 1) {
      setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
      }, 300);
    } else {
      if (onComplete) {
        onComplete();
      }
    }
  };

  const handleSkipAll = () => {
    if (!skipEnabled) return;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    setIsVisible(false);
    
    if (onComplete) {
      setTimeout(onComplete, 300);
    }
  };

  if (!dialogues || dialogues.length === 0 || !currentDialogue) {
    return null;
  }

  return (
    // Positioning/centering lives on this plain wrapper, not the motion.div
    // below: Framer Motion drives its animated element via an inline
    // `transform` (for the y slide), which would silently overwrite a CSS
    // `transform: translateX(-50%)` on the same element (inline style always
    // wins over a stylesheet) - that's what was knocking the subtitle off
    // center and clipping it against the right edge.
    <div className="subtitle-container">
      <AnimatePresence>
        {isVisible && (
          <motion.div
            className="subtitle-motion-wrapper"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
          >
            <div
              className={`subtitle-content ${isLastLine ? 'subtitle-content-dismissable' : ''}`}
              onClick={isLastLine ? handleDismissLast : undefined}
              role={isLastLine ? 'button' : undefined}
              tabIndex={isLastLine ? 0 : undefined}
            >
              {/* Optional - most rooms' dialogue lines have no `character` field at all (a
                  single implied first-person voice), so this renders nothing for them, same
                  as before. Rooms written as a back-and-forth (see roomDialogues.js's
                  captainsCabin) tag every line with who's speaking; the slug drives a color
                  per speaker (see SubtitleDisplay.css) so which of two alternating voices is
                  talking is obvious at a glance, not just from re-reading the label. */}
              {currentDialogue.character && (
                <div className={`subtitle-character subtitle-character--${currentDialogue.character.toLowerCase().replace(/\s+/g, '-')}`}>
                  {currentDialogue.character}
                </div>
              )}
              <div className="subtitle-text">{language ? pickBilingualLine(currentDialogue.text, language) : currentDialogue.text}</div>
              {isLastLine && <div className="subtitle-tap-hint">▸ Tap to continue</div>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default SubtitleDisplay;
